import { Component, Input, OnInit, inject, signal, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { FormInlineErrorComponent, SsnFieldComponent, State } from '@k12/forms';
import { CountiesService, CountyResponse } from '@k12/school-services';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-personal-address',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgxMaskDirective,
    FormInlineErrorComponent,
    SsnFieldComponent
  ],
  providers: [provideNgxMask()],
  templateUrl: './personal-address.component.html'
})
export class PersonalAddressComponent implements OnInit {
  @ViewChild('maskedSsnInput') maskedSsnInput?: ElementRef<HTMLInputElement>;

  @Input() formGroup!: FormGroup;
  @Input() states: State[] = [];
  @Input() showReviewStatus = true;

  private readonly countiesService = inject(CountiesService);

  counties: CountyResponse[] = [];
  isPersonalNorthCarolina = signal(false);
  filteredStates!: Observable<State[]>;
  filteredCounties!: Observable<CountyResponse[]>;
  showFullSsn = false;

  public readonly cityErrorMessages: Record<string, string | ((value: unknown) => string)> = {
    name: 'City must contain only letter and special characters'
  };

  get socialSecurityControl(): FormControl<string | null> {
    return this.formGroup.get('socialSecurityLast4') as FormControl<string | null>;
  }

  ngOnInit(): void {
    this.parseForDatePicker();

    this.filteredStates = this.formGroup.get('personalState')!.valueChanges.pipe(
      startWith(this.formGroup.get('personalState')!.value),
      map((value) => this._filterStates(value || ''))
    );

    this.formGroup
      .get('personalState')!
      .valueChanges.pipe(startWith(this.formGroup.get('personalState')!.value))
      .subscribe((state) => {
        let stateCode: string | undefined;

        if (typeof state === 'object' && state?.code) {
          stateCode = state.code;
        } else if (typeof state === 'string') {
          const matched = this.states.find((s) => s.code === state || s.name === state);
          stateCode = matched?.code;
        }

        this.isPersonalNorthCarolina.set(stateCode === 'NC');

        if (this.isPersonalNorthCarolina()) {
          this.loadCounties('NC');
        } else if (state != null && state !== '') {
          this.formGroup.patchValue({ personalCounty: '' }, { emitEvent: false });
        }
      });

    this.filteredCounties = this.formGroup.get('personalCounty')!.valueChanges.pipe(
      startWith(this.formGroup.get('personalCounty')!.value || ''),
      map((value: string) => this._filterCounties(value || ''))
    );
  }

  private _filterStates(value: string | State): State[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value?.name?.toLowerCase() || '';
    return this.states.filter((state) => state.name.toLowerCase().includes(filterValue));
  }

  private _filterCounties(value: string): CountyResponse[] {
    const filterValue = value.toLowerCase();
    return this.counties.filter((county) => county.name.toLowerCase().includes(filterValue));
  }

  displayStateName(state: State | null): string {
    return state?.name || '';
  }

  private loadCounties(state: string): void {
    this.countiesService.getCountiesByState(state).subscribe({
      next: (data: CountyResponse[]) => {
        this.counties = data;
        this.formGroup.get('personalCounty')!.updateValueAndValidity({ emitEvent: true });
      },
      error: () => {
        this.counties = [];
      }
    });
  }

  /**
   * Form is patched as MM/DD/YYYY by user-info-panel. The date picker needs a Date;
   * review mode binds the same control to ngx-mask, which only accepts a string.
   */
  private parseForDatePicker(): void {
    if (this.showReviewStatus) {
      return;
    }

    const control = this.formGroup.get('birthDate');
    const rawValue = control?.value;

    if (!control || rawValue == null || rawValue === '' || rawValue instanceof Date) {
      return;
    }

    const [month, day, year] = String(rawValue).trim().split('/').map(Number);
    if (!month || !day || !year) return;

    control.setValue(new Date(year, month - 1, day), { emitEvent: false });
  }
}
